// Справка „По артикули" (Справки → assortment; в режим „със склад" същата
// справка се казва „По рецепти" — виж `useAssortmentReportTitleKey`).
//
// Подредбата е като на останалите мигрирани справки: лента с периода и „Δ
// спрямо предишния период", четири кутийки с показателите и една карта със
// списъка. Списъкът стои върху споделената таблица (src/components/dataTable)
// в СЪРВЪРЕН режим — сортиране, филтри в колоните и страниране се вдигат до
// `GET /reports/analytics/ordered-items`, а редът „Общо" идва от агрегатите в
// същия отговор (страницата не може да сумира период, който не е зареден).
//
// Падащите менюта „Категория", „Станция" и „Тип" от стария екран ги няма —
// същите три филтъра живеят във фунийките на своите колони.

import { useEffect, useMemo, useState } from "react";
import { App } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";

import { useViewport } from "../../../hooks/useViewport";
import { useOrderedItems } from "../../../hooks/useReports";
import { useMenuCategories, useMenuStations } from "../../../hooks/useMenu";
import { ReportDatePicker } from "../../../components/ReportDatePicker";
import { useReportsDateParam } from "../useReportsDateParam";
import { useAssortmentReportTitleKey } from "../useAssortmentReportTitleKey";
import { useOrderedItemKindKeys } from "../useOrderedItemKindKeys";
import { LunchScopeToggle } from "./LunchScopeToggle";
import {
  kindsForLunchScope,
  LUNCH_SCOPE_PARAM,
  lunchScopeFromParam,
  type LunchScope,
} from "./lunchScope";
import {
  DataTable,
  parseDecimal,
  readPersistedPageSize,
  readPersistedSorting,
} from "../../../components/dataTable";
import type {
  ChecklistOption,
  ColumnFilterRule,
  SortRule,
} from "../../../components/dataTable";
import type {
  OrderedItemKind,
  OrderedItemsFilters,
  OrderedItemsSortBy,
  OrderedItemsSortOrder,
} from "../../../api/reports";
import type { Translation } from "../../../api/menu";
import ExportExcelButton from "./ExportExcelButton";
import { ReportTopBar } from "./ReportTopBar";
import { previousPeriodOf } from "./reportPeriod";
import { pluralLabel } from "./reportPlural";
import { ItemsKpis } from "./items/ItemsKpis";
import { ItemDaysModal } from "./items/ItemDaysModal";
import { useItemDaysDrilldown } from "./items/useItemDaysDrilldown";
import { useItemsColumns } from "./items/itemsColumns";
import type { NamedOrderedItem } from "./items/itemsColumns";
import { exportItems } from "./items/itemsExport";
import { ORDERED_ITEMS_MAX_PAGE_SIZE } from "./items/orderedItemsFetch";
import {
  businessDayEndFrom,
  businessDayStartFrom,
} from "../../../lib/businessDay";

/** Търсачката чака да спреш да пишеш, преди да пита сървъра. */
const SEARCH_DEBOUNCE_MS = 400;
/** Ключът, под който таблицата пази подредбата на колоните на потребителя. */
const ITEMS_TABLE_ID = "ordered-items";
/** Подредбата, с която справката тръгва, когато няма запомнена. */
const DEFAULT_SORTING: SortRule[] = [{ id: "total_sum", desc: true }];
/**
 * Ключовете, по които BE-то може да сортира (`OrderedItemsSortBy`). Запомнено
 * сортиране по колона извън списъка се изхвърля — иначе `sort_by` връща 422.
 */
const SERVER_SORTABLE_IDS: readonly string[] = [
  "name",
  "category_name",
  "station_name",
  "count",
  "avg_price",
  "total_sum",
  "is_addon",
];
/** Query параметърът, с който „По категории" отваря артикулите на една категория. */
const CATEGORY_PARAM = "category";

/** Отметнатите стойности на един чеклист филтър, като низове. */
const selectionOf = (
  columnFilters: ColumnFilterRule[],
  columnId: string,
): string[] => {
  const rule = columnFilters.find((filter) => filter.id === columnId);
  return Array.isArray(rule?.value) ? (rule.value as string[]) : [];
};

type Props = { onOpenMobileMenu?: () => void; isNarrow?: boolean };

export const AssortmentReport = ({
  onOpenMobileMenu,
  isNarrow = false,
}: Props = {}) => {
  const { t, i18n } = useTranslation();
  const { isNarrow: viewportNarrow } = useViewport();
  const narrow = isNarrow || viewportNarrow;
  const { message } = App.useApp();
  const langCode = i18n.language?.startsWith("en") ? "en" : "bg";

  // В режим „със склад" справката се казва „По рецепти".
  const reportTitleKey = useAssortmentReportTitleKey();
  const {
    addonKey,
    sellableKey,
    lunchKey,
    keyOf: kindKeyOf,
  } = useOrderedItemKindKeys();

  // Датата живее и в query параметъра ?date= — пази се при смяна на справка.
  const [dateRange, setDateRange] = useReportsDateParam(() => [
    businessDayStartFrom(dayjs()),
    businessDayEndFrom(dayjs()),
  ], "business");
  // Изключено по подразбиране: справката отговаря първо на въпроса „колко
  // е сега", а Δ-тата са втори въпрос и струват втора заявка за период,
  // който човек не е поискал.
  const [compare, setCompare] = useState(false);
  // „Всички / Без обедно / Само обедно" — стеснява `kinds` заедно с колоната „Тип"
  // (от „По категории" идва в ?lunch=, за да се отвори обхватът на кликнатия ред)
  const [lunchScope, setLunchScope] = useState<LunchScope>(() =>
    lunchScopeFromParam(new URLSearchParams(window.location.search).get(LUNCH_SCOPE_PARAM)),
  );
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Запомненото сортиране влиза И в заявката, не само в стрелката на хедъра.
  const [sorting, setSorting] = useState<SortRule[]>(() =>
    readPersistedSorting(ITEMS_TABLE_ID, DEFAULT_SORTING, SERVER_SORTABLE_IDS),
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFilterRule[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(() =>
    readPersistedPageSize(ITEMS_TABLE_ID, 20),
  );

  // „По категории" праща ?category=<id>. Филтърът НЕ се набутва в състоянието
  // на таблицата (тя няма вход за начални филтри) — стои като видим чип до
  // периода, за да е ясно откъде идва стесненият списък и как се маха.
  const [searchParams, setSearchParams] = useSearchParams();
  const pinnedCategoryRaw = searchParams.get(CATEGORY_PARAM);
  const pinnedCategoryId =
    pinnedCategoryRaw && /^\d+$/.test(pinnedCategoryRaw)
      ? Number(pinnedCategoryRaw)
      : null;

  const clearPinnedCategory = () => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete(CATEGORY_PARAM);
        next.delete(LUNCH_SCOPE_PARAM);
        return next;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(searchText.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [searchText]);

  const menuCategories = useMenuCategories();
  const menuStations = useMenuStations();

  const translate = useMemo(
    () => (translations: Translation[]) =>
      translations.find((item) => item.languageCode === langCode)?.name ??
      translations.find((item) => item.languageCode === "bg")?.name ??
      translations[0]?.name ??
      "",
    [langCode],
  );

  // Опциите идват от менюто, не от текущата страница: в сървърен режим
  // фасетите виждат само заредените редове и чеклистът би лъгал.
  const categoryOptions = useMemo<ChecklistOption[]>(
    () =>
      menuCategories
        .map((category) => ({
          value: String(category.id),
          label: translate(category.translations),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [menuCategories, translate],
  );

  const stationOptions = useMemo<ChecklistOption[]>(
    () =>
      menuStations
        .map((station) => ({
          value: String(station.id),
          label: translate(station.translations),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [menuStations, translate],
  );

  // Мемоизирани, за да не се пресъздава `filters` (а с него и заявката) на
  // всеки рендер само защото масивът е нов.
  const categorySelection = useMemo(
    () => selectionOf(columnFilters, "category_name"),
    [columnFilters],
  );
  const stationSelection = useMemo(
    () => selectionOf(columnFilters, "station_name"),
    [columnFilters],
  );
  const kindSelection = useMemo(
    () => selectionOf(columnFilters, "is_addon"),
    [columnFilters],
  );

  // Чипът отстъпва на фунийката: избере ли човек категории в колоната, те
  // важат, а чипът се скрива (URL параметърът остава, докато не се откачи).
  const showPinnedChip = pinnedCategoryId !== null && !categorySelection.length;

  const filters = useMemo<OrderedItemsFilters>(() => {
    const next: OrderedItemsFilters = {
      page: pageIndex + 1,
      page_size: Math.min(pageSize, ORDERED_ITEMS_MAX_PAGE_SIZE),
      language_code: langCode,
    };
    if (dateRange?.[0] && dateRange?.[1]) {
      next.start_date = businessDayStartFrom(dateRange[0]).format(
        "YYYY-MM-DD HH:mm:ss",
      );
      next.end_date = businessDayEndFrom(dateRange[1]).format("YYYY-MM-DD HH:mm:ss");
    }
    if (sorting.length) {
      next.sort_by = sorting[0].id as OrderedItemsSortBy;
      next.sort_order = (
        sorting[0].desc ? "desc" : "asc"
      ) as OrderedItemsSortOrder;
    }
    const categoryIds = categorySelection.length
      ? categorySelection.map(Number)
      : pinnedCategoryId !== null
        ? [pinnedCategoryId]
        : [];
    if (categoryIds.length) next.category_ids = categoryIds;
    if (stationSelection.length) {
      next.station_ids = stationSelection.map(Number);
    }
    const kinds = kindsForLunchScope<OrderedItemKind>(
      lunchScope,
      kindSelection as OrderedItemKind[],
      ["product", "lunch", "addon"],
      "lunch",
    );
    if (kinds.length) next.kinds = kinds;
    if (debouncedSearch) next.search = debouncedSearch;
    return next;
  }, [
    pageIndex,
    pageSize,
    langCode,
    dateRange,
    sorting,
    categorySelection,
    stationSelection,
    kindSelection,
    lunchScope,
    pinnedCategoryId,
    debouncedSearch,
  ]);

  // Предходният РАВЕН отрязък — само за Δ чиповете. Иска се една страница от
  // един ред: агрегатите BE-то смята върху целия филтриран набор, преди
  // страницирането. Тече ли още избраният период, краят се подрязва до
  // изтеклото (виж `reportPeriod`), иначе непълен ден стои срещу пълен.
  const previousFilters = useMemo<OrderedItemsFilters | null>(() => {
    if (!compare || !dateRange?.[0] || !dateRange?.[1]) return null;
    // Предходният период се строи от СЪЩИТЕ граници, с които се прави
    // заявката (`startOf`/`endOf` на деня), а не от суровите от календара.
    // Изборът без час връща край 00:00 и неравни прозорци: „01–03.09" се
    // сравняваше с два дни вместо три и Δ показваше ръст, какъвто няма.
    const previous = previousPeriodOf(
      businessDayStartFrom(dateRange[0]),
      businessDayEndFrom(dateRange[1]),
    );
    return {
      ...filters,
      page: 1,
      page_size: 1,
      start_date: previous.start.format("YYYY-MM-DD HH:mm:ss"),
      end_date: previous.end.format("YYYY-MM-DD HH:mm:ss"),
    };
  }, [compare, dateRange, filters]);

  const { data, isLoading, isFetching, refetch } = useOrderedItems(filters);
  // Без `enabled` в хука: при изключено сравнение се пита за същите филтри и
  // React Query го дава от кеша, без втора мрежова заявка.
  const { data: previousData } = useOrderedItems(previousFilters ?? filters);

  // Обедните филтри (превключвателят и „Обедно меню" в „Тип") има само когато
  // в периода е продадено нещо от обедно меню (`has_lunch` не зависи от `kinds`).
  const hasLunch = data?.has_lunch === true;
  const kindOptions = useMemo<ChecklistOption[]>(
    () => [
      { value: "product", label: t(sellableKey) },
      ...(hasLunch ? [{ value: "lunch", label: t(lunchKey) }] : []),
      { value: "addon", label: t(addonKey) },
    ],
    [t, sellableKey, addonKey, lunchKey, hasLunch],
  );
  // Период без обедни продажби → обедният избор отпада, иначе справката
  // остава празна без видим филтър, който да се махне
  const noLunchInPeriod = data != null && !data.has_lunch;
  useEffect(() => {
    if (!noLunchInPeriod) return;
    setLunchScope("all");
    const isKindRule = (rule: ColumnFilterRule) =>
      rule.id === "is_addon" && Array.isArray(rule.value);
    setColumnFilters((rules) =>
      rules.some((rule) => isKindRule(rule) && (rule.value as string[]).includes("lunch"))
        ? rules
            .map((rule) =>
              isKindRule(rule)
                ? { ...rule, value: (rule.value as string[]).filter((value) => value !== "lunch") }
                : rule,
            )
            .filter((rule) => !(isKindRule(rule) && (rule.value as string[]).length === 0))
        : rules,
    );
  }, [noLunchInPeriod]);

  // Смяна на периода, на търсенето или откачане на чипа връща на първа
  // страница — иначе човек стои на страница 7 от набор с три реда.
  useEffect(() => {
    setPageIndex(0);
  }, [dateRange, debouncedSearch, pinnedCategoryId, lunchScope]);

  const uniqueItems = data?.unique_items_count ?? 0;
  const quantity = parseDecimal(data?.total_quantity);
  const revenue = parseDecimal(data?.grand_total);

  const previousTotals = useMemo(() => {
    if (!previousFilters || !previousData) return null;
    return {
      quantity: parseDecimal(previousData.total_quantity),
      revenue: parseDecimal(previousData.grand_total),
    };
  }, [previousFilters, previousData]);

  // Имената и делът влизат в САМИТЕ редове — виж бележката в `itemsColumns`.
  const namedItems = useMemo<NamedOrderedItem[]>(
    () =>
      (data?.items ?? []).map((item) => {
        const itemRevenue = parseDecimal(item.total_sum);
        return {
          ...item,
          // Двата ключа са стабилни низове; `keyOf` от хука е нова функция на
          // всеки рендер и би пресъздавала целия масив без причина.
          kindLabel: t(
            item.is_addon ? addonKey : item.is_lunch ? lunchKey : sellableKey,
          ),
          quantity: parseDecimal(item.count),
          avgPrice: parseDecimal(item.avg_price),
          revenue: itemRevenue,
          sharePercent: revenue > 0 ? (itemRevenue / revenue) * 100 : 0,
        };
      }),
    [data?.items, revenue, t, addonKey, sellableKey, lunchKey],
  );

  const totals = useMemo(
    () => ({ count: quantity, total_sum: revenue }),
    [quantity, revenue],
  );

  const columns = useItemsColumns({
    categoryOptions,
    stationOptions,
    kindOptions,
  });

  const rowCount = data?.total ?? 0;

  // Клик по ред отваря дните му. Модалът пита СЪС същите филтри като
  // таблицата, затова сборът на дните му съвпада с реда, който е кликнат.
  const itemDays = useItemDaysDrilldown(filters);
  const selectedItem = itemDays.selectedItem;
  // Станцията и видът — двете неща, по които един и същ артикул има повече от
  // един ред; без тях заглавието „Кока-Кола" не казва кой точно ред е отворен.
  const selectedSubtitle = selectedItem
    ? [selectedItem.station_name, selectedItem.kindLabel]
        .filter(Boolean)
        .join(" · ")
    : "";

  const pinnedCategoryName =
    categoryOptions.find((option) => option.value === String(pinnedCategoryId))
      ?.label ?? `#${pinnedCategoryId}`;

  const handleExport = async () => {
    try {
      await exportItems(filters, {
        t,
        kindLabelOf: (row) => t(kindKeyOf(row)),
        reportName: t(reportTitleKey),
      });
    } catch {
      message.error(t("reportsPage.exportFailed"));
    }
  };

  return (
    <div className="flex h-full flex-col bg-canvas">
      <ReportTopBar
        title={t(reportTitleKey)}
        isNarrow={narrow}
        onOpenMobileMenu={onOpenMobileMenu}
        onRefresh={() => refetch()}
        isFetching={isFetching}
      />

      <div
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
          narrow ? "px-3.5 pb-6 pt-3.5" : "px-7 pb-8 pt-[22px]"
        }`}
      >
        <div className="flex flex-col gap-3.5">
          <div className="flex flex-wrap items-center justify-center gap-2 overflow-x-auto pb-0.5">
            <ReportDatePicker
              dayMode="business"
              value={dateRange}
              onChange={setDateRange}
              defaultMode="day"
            />
            <button
              type="button"
              onClick={() => setCompare((previous) => !previous)}
              aria-pressed={compare}
              className={`h-9 shrink-0 rounded-lg border px-3.5 text-[12.5px] font-bold transition-colors ${
                compare
                  ? "border-brand-tint2 bg-brand-tint text-brand-deep-ink"
                  : "border-shade-3 bg-surface text-soft hover:bg-fill"
              }`}
            >
              {compare ? "✓ " : ""}
              {t("reportsPage.billsHistoryComparePrevious")}
            </button>
            {hasLunch ? (
              <LunchScopeToggle value={lunchScope} onChange={setLunchScope} />
            ) : null}
            {showPinnedChip ? (
              <button
                type="button"
                onClick={clearPinnedCategory}
                title={t("reportsPage.itemsCategoryFilterClear")}
                className="flex h-9 shrink-0 items-center gap-2 rounded-lg border border-brand-tint2 bg-brand-tint px-3.5 text-[12.5px] font-bold text-brand-deep-ink transition-colors hover:bg-brand-tint2"
              >
                {t("reportsPage.itemsCategoryFilterChip", {
                  name: pinnedCategoryName,
                })}
                <CloseOutlined className="text-[10px]" />
              </button>
            ) : null}
          </div>

          <ItemsKpis
            uniqueItems={uniqueItems}
            quantity={quantity}
            revenue={revenue}
            previous={previousTotals}
            isNarrow={narrow}
          />

          <DataTable<NamedOrderedItem>
            tableId={ITEMS_TABLE_ID}
            data={namedItems}
            columns={columns}
            // Само `item_id` НЕ е уникален: продаваемият има отделен ред за
            // всяка станция и за всеки вид (продукт и добавка).
            getRowId={(item) =>
              `${item.item_id}-${item.station_id}-${item.is_addon ? "a" : item.is_lunch ? "l" : "p"}`
            }
            mode="server"
            server={{
              rowCount,
              pageIndex,
              onPageIndexChange: setPageIndex,
              onPageSizeChange: setPageSize,
              onSortingChange: setSorting,
              onColumnFiltersChange: setColumnFilters,
              totals,
            }}
            loading={isLoading}
            refreshing={isFetching && !isLoading}
            ariaLabel={t(reportTitleKey)}
            onRowClick={itemDays.openItem}
            title={
              <div className="flex min-w-0 flex-col gap-1">
                <span className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
                  {t("reportsPage.itemsTableKicker")}
                </span>
                <span className="text-[16px] font-extrabold leading-none text-ink">
                  {pluralLabel(
                    t,
                    rowCount,
                    "reportsPage.assortmentResultOne",
                    "reportsPage.assortmentResults",
                  )}
                  <span className="font-medium text-muted">
                    {` · ${t("reportsPage.itemsTableHint")}`}
                  </span>
                </span>
              </div>
            }
            search={{
              value: searchText,
              onChange: setSearchText,
              placeholder: t("reportsPage.assortmentSearchPlaceholder"),
            }}
            toolbarRight={
              <ExportExcelButton
                onExport={handleExport}
                disabled={rowCount === 0}
                bare
              />
            }
            emptyTitle={t("reportsPage.assortmentNoItems")}
            emptyHint={t("reportsPage.itemsEmptyHint")}
            pageSize={pageSize}
            pageSizeOptions={[10, 20, 50, 100, 200]}
            initialSorting={DEFAULT_SORTING}
          />
        </div>
      </div>

      <ItemDaysModal
        open={selectedItem !== null}
        title={selectedItem?.name ?? ""}
        subtitle={selectedSubtitle}
        days={itemDays.days}
        totalQuantity={itemDays.totalQuantity}
        grandTotal={itemDays.grandTotal}
        isLoading={itemDays.isLoading}
        isError={itemDays.isError}
        onClose={itemDays.closeItem}
      />
    </div>
  );
};
